/**
 * Firebase Chat Service
 * Clean & Correct Implementation
 */

import { initializeApp } from 'firebase/app';
import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';

/* =========================================
   Firebase Configuration
========================================= */

const firebaseConfig = {
  apiKey: "AIzaSyBjD9KHuVjUNSvPpa6y-pElD7lIElCiXmE",
  authDomain: "my-chat-box-ec5b0.firebaseapp.com",
  projectId: "my-chat-box-ec5b0",
  storageBucket: "my-chat-box-ec5b0.firebasestorage.app",
  messagingSenderId: "387721645160",
  appId: "1:387721645160:web:64f6ec464447b49ea6bfdd",
  measurementId: "G-CLCBRJYNMN"
};

/* =========================================
   Lazy Init
========================================= */

let app = null;
let db = null;
let storage = null;
let auth = null;
let firebaseAuthUid = null;
let firebaseAuthPromise = null;

const getLoggedInUserId = () => {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) return null;
    const user = JSON.parse(raw);
    return user?.id != null ? String(user.id) : null;
  } catch {
    return null;
  }
};

/** Sign in to Firebase Auth with backend custom token (uid = app user id). */
export const ensureFirebaseAuth = async (userId = null) => {
  const uid = userId != null ? String(userId) : getLoggedInUserId();
  if (!uid) {
    throw new Error('Login required for chat');
  }

  await initializeFirebase();

  if (auth?.currentUser?.uid === uid) {
    firebaseAuthUid = uid;
    return auth.currentUser;
  }

  if (firebaseAuthPromise && firebaseAuthUid === uid) {
    return firebaseAuthPromise;
  }

  firebaseAuthPromise = (async () => {
    const { chatAPI } = await import('./api.service');
    try {
      const response = await chatAPI.getFirebaseToken();
      const customToken = response?.data?.token;
      if (response?.success === false || !customToken) {
        throw new Error(response?.message || 'Failed to get Firebase auth token');
      }

      if (!auth) {
        auth = getAuth(app);
      }

      if (auth.currentUser && auth.currentUser.uid !== uid) {
        try {
          await signOut(auth);
        } catch (e) {
          console.warn('Firebase signOut before re-auth:', e);
        }
      }

      const credential = await signInWithCustomToken(auth, customToken);
      // Ensure ID token is ready before Firestore reads/writes (avoids permission-denied race).
      await credential.user.getIdToken();
      firebaseAuthUid = uid;
      return credential.user;
    } catch (err) {
      const msg = err?.message || 'Failed to get Firebase auth token';
      console.error('Chat Firebase auth failed:', msg, err);
      throw new Error(msg);
    }
  })();

  try {
    return await firebaseAuthPromise;
  } finally {
    firebaseAuthPromise = null;
  }
};

const initializeFirebase = async () => {
  try {
    if (db && storage && app) {
      return { app, db, storage, auth };
    }
    
    if (!app) {
      console.log('🔥 Initializing Firebase app...');
      app = initializeApp(firebaseConfig);
      console.log('✅ Firebase app initialized');
    }
    
    if (!db) {
      console.log('🔥 Initializing Firestore...');
      db = getFirestore(app);
      console.log('✅ Firestore initialized');
    }
    
    if (!storage) {
      console.log('🔥 Initializing Firebase Storage...');
      storage = getStorage(app);
      console.log('✅ Firebase Storage initialized');
    }

    if (!auth) {
      auth = getAuth(app);
    }
    
    return { app, db, storage, auth };
  } catch (error) {
    console.error('❌ Firebase initialization error:', error);
    throw new Error(`Failed to initialize Firebase: ${error.message}`);
  }
};

const initChatFirestore = async (userId = null) => {
  await initializeFirebase();
  await ensureFirebaseAuth(userId);
  return db;
};

/* =========================================
   Chat Room ID (DETERMINISTIC)
   Format: min(buyerId, posterId)_max(buyerId, posterId)_propertyId
   Ensures exactly one chat per (buyerId + propertyId + posterId)
========================================= */

export const generateChatRoomId = (
  buyerId,
  posterId,
  propertyId
) => {
  // Convert to strings for consistent comparison
  const buyerIdStr = String(buyerId);
  const posterIdStr = String(posterId);
  
  // Sort IDs to ensure deterministic chat room ID
  const [minId, maxId] = buyerIdStr < posterIdStr 
    ? [buyerIdStr, posterIdStr] 
    : [posterIdStr, buyerIdStr];
  
  return `${minId}_${maxId}_${propertyId}`;
};

/* =========================================
   Create / Get Chat Room (ONLY PLACE)
   Creates chat room ONLY when first message is sent
   Uses deterministic chat room ID based on sorted participant IDs
========================================= */

export const createOrGetChatRoom = async (
  buyerId,
  posterId,
  posterRole,
  propertyId
) => {
  const buyerIdStr = String(buyerId);
  const posterIdStr = String(posterId);
  await initChatFirestore(buyerIdStr);
  const chatDb = db;

  // Generate deterministic chat room ID with sorted IDs
  const chatRoomId = generateChatRoomId(
    buyerId,
    posterId,
    propertyId
  );

  const chatRoomRef = doc(db, 'chats', chatRoomId);

  const [minId, maxId] = buyerIdStr < posterIdStr
    ? [buyerIdStr, posterIdStr]
    : [posterIdStr, buyerIdStr];
  const expectedParticipants = [minId, maxId];

  let snapshot = null;
  try {
    snapshot = await getDoc(chatRoomRef);
  } catch (readErr) {
    // Older rules blocked get on missing docs — fall through to create/merge
    console.warn('Chat room getDoc failed, will create/merge:', readErr?.code || readErr?.message);
    snapshot = null;
  }

  if (snapshot?.exists()) {
    const data = snapshot.data() || {};
    const existing = Array.isArray(data.participants)
      ? data.participants.map(String)
      : [];
    const authUid = auth?.currentUser?.uid || buyerIdStr;
    const missingSelf = !existing.includes(String(authUid));
    const needsRepair =
      missingSelf ||
      !existing.includes(buyerIdStr) ||
      !existing.includes(posterIdStr);

    if (needsRepair) {
      const merged = Array.from(new Set([...existing, ...expectedParticipants].map(String)));
      await updateDoc(chatRoomRef, {
        participants: merged,
        buyerId: data.buyerId || buyerIdStr,
        receiverId: data.receiverId || posterIdStr,
        receiverRole: data.receiverRole || posterRole || 'seller',
        propertyId: String(data.propertyId || propertyId),
        updatedAt: serverTimestamp(),
      });
    } else {
      await updateDoc(chatRoomRef, {
        updatedAt: serverTimestamp()
      });
    }
    return chatRoomId;
  }

  // Create new chat room with sorted participants (merge-safe if race)
  await setDoc(chatRoomRef, {
    buyerId: buyerIdStr,
    receiverId: posterIdStr,
    receiverRole: posterRole || 'seller', // 'seller' | 'agent'
    propertyId: String(propertyId),

    participants: expectedParticipants,

    lastMessage: '',

    readStatus: {
      [buyerIdStr]: 'new',
      [posterIdStr]: 'new'
    },

    readBy: {},
    lastReadAt: {},

    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }, { merge: true });

  return chatRoomId;
};

/* =========================================
   Send Message (NO AUTO ROOM CREATION)
========================================= */

export const sendMessage = async (
  chatRoomId,
  senderId,
  senderRole,
  text
) => {
  if (!chatRoomId || !senderId || !senderRole || !text || !text.trim()) {
    throw new Error('Missing required parameters for sendMessage');
  }

  try {
    const senderIdStr = String(senderId);
    await initChatFirestore(senderIdStr);
  const chatDb = db;

    const chatRoomRef = doc(db, 'chats', chatRoomId);
    const roomSnap = await getDoc(chatRoomRef);

    if (!roomSnap.exists()) {
      throw new Error('Chat room does not exist');
    }

    const authUid = auth?.currentUser?.uid || senderIdStr;
    const roomData = roomSnap.data() || {};
    const participants = Array.isArray(roomData.participants)
      ? roomData.participants.map(String)
      : [];

    // Repair participants if auth uid is missing (common after Open Chat race)
    if (!participants.includes(String(authUid))) {
      const repaired = Array.from(new Set([...participants, String(authUid), senderIdStr]));
      await updateDoc(chatRoomRef, {
        participants: repaired,
        updatedAt: serverTimestamp(),
      });
    }

    const messagesRef = collection(chatRoomRef, 'messages');

    await addDoc(messagesRef, {
      senderId: authUid,
      senderRole,
      text: text.trim(),
      timestamp: serverTimestamp()
    });

    const otherUser =
      String(authUid) === String(roomData.buyerId)
        ? String(roomData.receiverId)
        : String(roomData.buyerId);

    await updateDoc(chatRoomRef, {
      lastMessage: text.trim(),
      updatedAt: serverTimestamp(),
      [`readStatus.${otherUser}`]: 'new'
    });
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/* =========================================
   Listen Messages
========================================= */

export const listenToMessages = (chatRoomId, callback) => {
  if (!chatRoomId) {
    console.error('listenToMessages: chatRoomId is required');
    callback([], new Error('chatRoomId is required'));
    return () => {};
  }

  let unsubscribe = () => {};
  let isActive = true;

  initChatFirestore()
    .then(() => {
      if (!isActive || !db) return;

      const messagesRef = collection(
        doc(db, 'chats', chatRoomId),
        'messages'
      );

      const q = query(messagesRef, orderBy('timestamp', 'asc'));

      unsubscribe = onSnapshot(
        q,
        snapshot => {
          if (!isActive) return;
          
          try {
            const messages = snapshot.docs.map(d => {
              const data = d.data();
              let timestamp = new Date();
              
              if (data.timestamp) {
                if (data.timestamp.toDate && typeof data.timestamp.toDate === 'function') {
                  timestamp = data.timestamp.toDate();
                } else if (data.timestamp instanceof Date) {
                  timestamp = data.timestamp;
                } else if (typeof data.timestamp === 'string' || typeof data.timestamp === 'number') {
                  timestamp = new Date(data.timestamp);
                }
              }

              return {
                id: d.id,
                ...data,
                timestamp: timestamp
              };
            });
            callback(messages, null);
          } catch (error) {
            console.error('Error processing messages:', error);
            callback([], error);
          }
        },
        error => {
          if (!isActive) return;
          console.error('Firebase listener error:', error);
          callback([], error);
        }
      );
    })
    .catch(error => {
      if (!isActive) return;
      console.error('Firebase initialization error:', error);
      callback([], error);
    });

  // Return cleanup function
  return () => {
    isActive = false;
    if (unsubscribe && typeof unsubscribe === 'function') {
      unsubscribe();
    }
  };
};

/* =========================================
   Get Chat Rooms (ALL ROLES)
========================================= */

export const getUserChatRooms = async userId => {
  await initChatFirestore(userId);
  const uidStr = String(userId);
  const uidNum = Number(userId);
  const hasNumericId = !Number.isNaN(uidNum) && String(uidNum) === uidStr;
  const roomMap = new Map();

  const collect = (snapshot) => {
    if (!snapshot?.docs) return;
    snapshot.docs.forEach((d) => {
      if (!roomMap.has(d.id)) {
        roomMap.set(d.id, {
          id: d.id,
          ...d.data(),
          updatedAt: d.data().updatedAt?.toDate?.() || new Date(0),
        });
      }
    });
  };

  const runQuery = async (constraints, label) => {
    try {
      const q = query(collection(db, 'chats'), ...constraints);
      collect(await getDocs(q));
    } catch (err) {
      console.error(`getUserChatRooms (${label}) failed:`, err?.code || err?.message);
    }
  };

  // Current format
  await runQuery([where('participants', 'array-contains', uidStr)], 'participants-string');
  // Older numeric participants
  if (hasNumericId) {
    await runQuery([where('participants', 'array-contains', uidNum)], 'participants-number');
  }
  // Fallback by buyerId / receiverId (covers rooms with missing/mismatched participants)
  await runQuery([where('buyerId', '==', uidStr)], 'buyerId-string');
  await runQuery([where('receiverId', '==', uidStr)], 'receiverId-string');
  if (hasNumericId) {
    await runQuery([where('buyerId', '==', uidNum)], 'buyerId-number');
    await runQuery([where('receiverId', '==', uidNum)], 'receiverId-number');
  }

  const rooms = Array.from(roomMap.values());
  rooms.sort((a, b) => b.updatedAt - a.updatedAt);
  return rooms;
};

/* =========================================
   Read Status
========================================= */

export const markChatAsRead = async (chatRoomId, userId) => {
  await initChatFirestore();
  const chatDb = db;

  await updateDoc(doc(db, 'chats', chatRoomId), {
    [`readStatus.${userId}`]: 'read',
    [`readBy.${userId}`]: serverTimestamp(),
    [`lastReadAt.${userId}`]: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const getInquiryReadStatus = async (chatRoomId, userId) => {
  await initChatFirestore();
  const chatDb = db;

  const chatRoomRef = doc(db, 'chats', chatRoomId);
  const snapshot = await getDoc(chatRoomRef);

  if (!snapshot.exists()) {
    return { status: null };
  }

  const data = snapshot.data();
  const readStatus = data.readStatus || {};
  const status = readStatus[userId] || null;

  return { status };
};

export const updateInquiryReadStatus = async (chatRoomId, userId, status) => {
  await initChatFirestore();
  const chatDb = db;

  await updateDoc(doc(db, 'chats', chatRoomId), {
    [`readStatus.${userId}`]: status,
    [`readBy.${userId}`]: serverTimestamp(),
    [`lastReadAt.${userId}`]: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

/* =========================================
   Get Chat Room Details
========================================= */

export const getChatRoomDetails = async chatRoomId => {
  await initChatFirestore();
  const chatDb = db;
  try {
    const snap = await getDoc(doc(db, 'chats', chatRoomId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (err) {
    // Missing room under strict rules can surface as permission-denied
    if (err?.code === 'permission-denied') {
      return null;
    }
    throw err;
  }
};

/* =========================================
   Property Image Upload to Firebase Storage
========================================= */

/**
 * Upload property image to Firebase Storage
 * @param {File} file - Image file to upload
 * @param {number} userId - User ID (for organizing storage)
 * @param {number|null} propertyId - Property ID (null for temp upload)
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise<{url: string, path: string, fileName: string}>} Firebase download URL
 */
export const uploadPropertyImageToFirebase = async (
  file,
  userId,
  propertyId = null,
  onProgress = null
) => {
  try {
    console.log('🔥 Firebase Upload: Starting upload...', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      userId,
      propertyId
    });
    
    // Initialize Firebase
    const { storage } = await initializeFirebase();
    if (!storage) {
      throw new Error('Firebase Storage not initialized');
    }
    console.log('✅ Firebase Storage initialized');
    
    // Generate unique filename
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 15);
    const extension = file.name.split('.').pop() || 'jpg';
    const fileName = `img_${timestamp}_${randomId}.${extension}`;
    
    // Determine storage path
    // Temporary uploads go to: properties/temp/{userId}/{filename}
    // Permanent uploads go to: properties/{propertyId}/{filename}
    let storagePath;
    if (propertyId) {
      storagePath = `properties/${propertyId}/${fileName}`;
    } else {
      storagePath = `properties/temp/${userId}/${fileName}`;
    }
    
    console.log('🔥 Firebase Upload: Storage path:', storagePath);
    
    // Create storage reference
    const storageRef = ref(storage, storagePath);
    console.log('✅ Firebase Storage reference created');
    
    // Upload file
    console.log('🔥 Firebase Upload: Uploading file to Firebase...');
    const snapshot = await uploadBytes(storageRef, file);
    console.log('✅ Firebase Upload: File uploaded successfully', {
      bytesTransferred: snapshot.metadata.size,
      contentType: snapshot.metadata.contentType
    });
    
    // Get download URL
    console.log('🔥 Firebase Upload: Getting download URL...');
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('✅ Firebase Upload: Download URL obtained:', downloadURL);
    
    return {
      url: downloadURL,
      path: storagePath,
      fileName: fileName
    };
  } catch (error) {
    console.error('❌ Firebase Upload Error:', {
      message: error.message,
      code: error.code,
      stack: error.stack,
      fileName: file?.name,
      userId,
      propertyId
    });
    
    // Provide more specific error messages
    if (error.code === 'storage/unauthorized') {
      throw new Error('Firebase Storage: Permission denied. Check Storage rules.');
    } else if (error.code === 'storage/quota-exceeded') {
      throw new Error('Firebase Storage: Quota exceeded. Check Firebase billing.');
    } else if (error.code === 'storage/unauthenticated') {
      throw new Error('Firebase Storage: User not authenticated.');
    } else if (error.message.includes('network')) {
      throw new Error('Firebase Storage: Network error. Check internet connection.');
    }
    
    throw new Error(`Failed to upload image to Firebase: ${error.message}`);
  }
};

/* =========================================
   Profile Image Upload to Firebase Storage
========================================= */

/**
 * Upload profile image to Firebase Storage
 * @param {File} file - Image file to upload
 * @param {number|string} userId - User ID (for organizing storage)
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise<{url: string, path: string, fileName: string}>} Firebase download URL
 */
export const uploadProfileImageToFirebase = async (
  file,
  userId,
  onProgress = null
) => {
  try {
    console.log('🔥 Firebase Profile Upload: Starting upload...', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      userId,
    });
    
    // Initialize Firebase
    const { storage } = await initializeFirebase();
    if (!storage) {
      throw new Error('Firebase Storage not initialized');
    }
    console.log('✅ Firebase Storage initialized for profile image');
    
    // Generate unique filename
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 15);
    const extension = file.name.split('.').pop() || 'jpg';
    const fileName = `profile_${userId}_${timestamp}_${randomId}.${extension}`;
    
    // Profile images go to: profiles/{userId}/{filename}
    const storagePath = `profiles/${userId}/${fileName}`;
    console.log('🔥 Firebase Profile Upload: Storage path:', storagePath);
    
    // Create storage reference
    const storageRef = ref(storage, storagePath);
    console.log('✅ Firebase Profile Storage reference created');
    
    // Upload file
    console.log('🔥 Firebase Profile Upload: Uploading file to Firebase...');
    const snapshot = await uploadBytes(storageRef, file);
    console.log('✅ Firebase Profile Upload: File uploaded successfully', {
      bytesTransferred: snapshot.metadata.size,
      contentType: snapshot.metadata.contentType
    });
    
    // Get download URL
    console.log('🔥 Firebase Profile Upload: Getting download URL...');
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('✅ Firebase Profile Upload: Download URL obtained:', downloadURL);
    
    return {
      url: downloadURL,
      path: storagePath,
      fileName: fileName
    };
  } catch (error) {
    console.error('❌ Firebase Profile Upload Error:', {
      message: error.message,
      code: error.code,
      stack: error.stack,
      fileName: file?.name,
      userId,
    });
    
    if (error.code === 'storage/unauthorized') {
      throw new Error('Firebase Storage: Permission denied. Check Storage rules.');
    } else if (error.code === 'storage/quota-exceeded') {
      throw new Error('Firebase Storage: Quota exceeded. Check Firebase billing.');
    } else if (error.code === 'storage/unauthenticated') {
      throw new Error('Firebase Storage: User not authenticated.');
    } else if (error.message.includes('network')) {
      throw new Error('Firebase Storage: Network error. Check internet connection.');
    }
    
    throw new Error(`Failed to upload profile image to Firebase: ${error.message}`);
  }
};

/**
 * Delete image from Firebase Storage (optional utility)
 * @param {string} storagePath - Firebase Storage path
 */
export const deletePropertyImageFromFirebase = async (storagePath) => {
  try {
    const { storage } = await initializeFirebase();
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Firebase delete error:', error);
    // Don't throw - deletion failures are not critical
  }
};

/* =========================================
   Export
========================================= */

export default {
  initializeFirebase,
  ensureFirebaseAuth,
  generateChatRoomId,
  createOrGetChatRoom,
  sendMessage,
  listenToMessages,
  getUserChatRooms,
  markChatAsRead,
  getChatRoomDetails,
  getInquiryReadStatus,
  updateInquiryReadStatus,
  uploadPropertyImageToFirebase,
  deletePropertyImageFromFirebase,
  uploadProfileImageToFirebase
};
